import { useCallback, useEffect, useRef, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import {
  getClient,
  callPlatformAdmin,
  sendTestSms,
  readTemplateType,
  ALIYUN_SMS_PRESET,
  SMS_TEMPLATE_TYPES,
  type AdSlot,
  type AuditLog,
  type AuthProviderSetting,
  type PlatformActivity,
  type PlatformAd,
  type PlatformAlert,
  type PlatformUser,
  type SensitiveWord,
} from "./platformAdminProvider";
import { PasswordChangeForm } from "./PasswordChangeForm";
import { validateOfficialActivity } from "./adminUiValidation";

type Page = "overview" | "users" | "activities" | "alerts" | "words" | "ads" | "audit" | "settings";
type Overview = {
  users: number;
  active_users: number;
  pending_activities: number;
  open_alerts: number;
  backup_users: number;
};

const labels: Record<Page, string> = {
  overview: "平台概览",
  users: "用户管理",
  activities: "活动审核",
  alerts: "内容预警",
  words: "敏感词",
  ads: "广告管理",
  audit: "审计日志",
  settings: "登录与短信 / 账号安全",
};

const activityStatusLabel: Record<PlatformActivity["status"], string> = {
  draft: "草稿",
  pending: "待审核",
  approved: "已发布",
  rejected: "已驳回",
  removed: "已下架",
};

const alertStatusLabel: Record<PlatformAlert["status"], string> = {
  open: "未处理",
  acknowledged: "已确认",
  resolved: "已解决",
};

export function PlatformAdminApp() {
  const client = getClient();
  const [session, setSession] = useState<Session | null>(null);
  const [page, setPage] = useState<Page>("overview");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [overview, setOverview] = useState<Overview | null>(null);
  const [users, setUsers] = useState<PlatformUser[]>([]);
  const [activities, setActivities] = useState<PlatformActivity[]>([]);
  const [alerts, setAlerts] = useState<PlatformAlert[]>([]);
  const [words, setWords] = useState<SensitiveWord[]>([]);
  const [providers, setProviders] = useState<AuthProviderSetting[]>([]);
  const [slots, setSlots] = useState<AdSlot[]>([]);
  const [ads, setAds] = useState<PlatformAd[]>([]);
  const [logs, setLogs] = useState<AuditLog[]>([]);

  useEffect(() => {
    let active = true;
    client.auth
      .getSession()
      .then(({ data }) => active && setSession(data.session));
    const { data } = client.auth.onAuthStateChange((_event, next) =>
      setSession(next),
    );
    return () => {
      active = false;
      data.subscription.unsubscribe();
    };
  }, [client]);

  const refresh = useCallback(async (target: Page = page) => {
    if (!session) return;
    setLoading(true);
    setError("");
    try {
      if (target === "overview")
        setOverview(await callPlatformAdmin<Overview>(session, "overview"));
      if (target === "users")
        setUsers(
          await callPlatformAdmin<PlatformUser[]>(session, "list_users"),
        );
      if (target === "activities")
        setActivities(
          await callPlatformAdmin<PlatformActivity[]>(
            session,
            "list_activities",
          ),
        );
      if (target === "alerts")
        setAlerts(
          await callPlatformAdmin<PlatformAlert[]>(session, "list_alerts"),
        );
      if (target === "words")
        setWords(
          await callPlatformAdmin<SensitiveWord[]>(
            session,
            "list_sensitive_words",
          ),
        );
      if (target === "ads") {
        setSlots(await callPlatformAdmin<AdSlot[]>(session, "list_ad_slots"));
        setAds(await callPlatformAdmin<PlatformAd[]>(session, "list_ads"));
      }
      if (target === "audit")
        setLogs(
          await callPlatformAdmin<AuditLog[]>(session, "list_audit_logs"),
        );
      if (target === "settings")
        setProviders(
          await callPlatformAdmin<AuthProviderSetting[]>(
            session,
            "list_auth_providers",
          ),
        );
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "读取数据失败");
    } finally {
      setLoading(false);
    }
  }, [page, session]);

  useEffect(() => {
    void refresh(page);
  }, [page, refresh, session]);

  if (!session) return <AdminLogin onLoggedIn={(next) => setSession(next)} />;

  const signOut = async () => {
    await client.auth.signOut();
    setSession(null);
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <header className="border-b bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-teal-700">
              知脉Relink
            </p>
            <h1 className="text-xl font-semibold">平台管理后台</h1>
          </div>
          <button
            className="rounded-lg border px-3 py-2 text-sm hover:bg-slate-50"
            onClick={signOut}
          >
            退出登录
          </button>
        </div>
      </header>
      <div className="mx-auto flex max-w-7xl gap-6 px-6 py-6">
        <nav className="w-48 shrink-0 space-y-1">
          {(Object.keys(labels) as Page[]).map((item) => (
            <button
              key={item}
              onClick={() => setPage(item)}
              className={`w-full rounded-lg px-3 py-2 text-left text-sm ${page === item ? "bg-teal-700 text-white" : "hover:bg-white"}`}
            >
              {labels[item]}
            </button>
          ))}
          <div className="mt-6 rounded-lg border bg-white p-3 text-xs leading-5 text-slate-500">
            私人联系人、关系档案与通讯录不在后台展示，仅治理平台公开内容。
          </div>
        </nav>
        <main className="min-w-0 flex-1">
          {error && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}
          {loading && (
            <div className="mb-4 text-sm text-slate-500">加载中……</div>
          )}
          {page === "overview" && (
            <OverviewPage overview={overview} onGo={setPage} onRefresh={() => refresh("overview")} />
          )}
          {page === "users" && (
            <UsersPage users={users} session={session} onRefresh={() => refresh("users")} />
          )}
          {page === "activities" && (
            <ActivitiesPage activities={activities} session={session} onRefresh={() => refresh("activities")} />
          )}
          {page === "alerts" && (
            <AlertsPage alerts={alerts} session={session} onRefresh={() => refresh("alerts")} />
          )}
          {page === "words" && (
            <WordsPage words={words} onRefresh={() => refresh("words")} session={session} />
          )}
          {page === "ads" && (
            <AdsPage slots={slots} ads={ads} session={session} onRefresh={() => refresh("ads")} />
          )}
          {page === "audit" && <AuditPage logs={logs} onRefresh={() => refresh("audit")} />}
          {page === "settings" && (
            <SettingsPage providers={providers} session={session} onRefresh={() => refresh("settings")} />
          )}
        </main>
      </div>
    </div>
  );
}

function AdminLogin({
  onLoggedIn,
}: {
  onLoggedIn: (session: Session) => void;
}) {
  const client = getClient();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      const result = await client.auth.signInWithPassword({ email, password });
      if (result.error || !result.data.session)
        setError(result.error?.message ?? "管理员登录失败");
      else onLoggedIn(result.data.session);
    } catch {
      setError(
        "无法连接账号服务，请先启动本地 Supabase（54331）或检查网络配置",
      );
    }
    setLoading(false);
  };
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <form
        onSubmit={submit}
        className="w-full max-w-md rounded-2xl border bg-white p-8 shadow-sm"
      >
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-teal-700">
          知脉Relink
        </p>
        <h1 className="mt-2 text-2xl font-semibold">平台管理后台</h1>
        <p className="mt-2 text-sm text-slate-500">
          仅限平台管理员登录。用户私人数据不会出现在这里。
        </p>
        <label className="mt-6 block text-sm font-medium">
          管理员邮箱
          <input
            className="mt-2 w-full rounded-lg border px-3 py-2"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
          />
        </label>
        <label className="mt-4 block text-sm font-medium">
          密码
          <input
            className="mt-2 w-full rounded-lg border px-3 py-2"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
          />
        </label>
        {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
        <button
          className="mt-6 w-full rounded-lg bg-teal-700 px-4 py-2.5 font-medium text-white disabled:opacity-50"
          disabled={loading}
        >
          {loading ? "登录中……" : "登录"}
        </button>
      </form>
    </div>
  );
}

function OverviewPage({ overview, onGo, onRefresh }: { overview: Overview | null; onGo: (p: Page) => void; onRefresh: () => Promise<void> }) {
  return (
    <section>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold">平台概览</h2>
          <p className="mt-1 text-sm text-slate-500">
            这里只显示平台治理数据，不包含用户私人关系资料。云端不存储联系人，只统计最后备份时间。
          </p>
        </div>
        <button className="rounded-lg border px-3 py-2 text-sm" onClick={() => void onRefresh()}>
          刷新
        </button>
      </div>
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {[
          ["用户总数", overview?.users ?? "—", "users" as Page],
          ["活跃用户", overview?.active_users ?? "—", "users" as Page],
          ["待审核活动", overview?.pending_activities ?? "—", "activities" as Page],
          ["未处理预警", overview?.open_alerts ?? "—", "alerts" as Page],
          ["已备份用户", overview?.backup_users ?? "—", "overview" as Page],
        ].map(([label, value, target]) => (
          <button key={label as string} onClick={() => onGo(target as Page)} className="rounded-xl border bg-white p-5 text-left hover:shadow-sm">
            <p className="text-sm text-slate-500">{label}</p>
            <p className="mt-2 text-3xl font-semibold">{value}</p>
          </button>
        ))}
      </div>
      <div className="mt-6 rounded-xl border bg-white p-5 text-sm leading-6 text-slate-600">
        治理闭环：用户提交公开活动 → 云端敏感词筛查 → 命中生成预警并驳回 → 管理员在活动审核中处理 →
        所有操作写入审计日志。广告位默认关闭，启用后才对用户可见。
      </div>
    </section>
  );
}

function UsersPage({
  users,
  session,
  onRefresh,
}: {
  users: PlatformUser[];
  session: Session;
  onRefresh: () => Promise<void>;
}) {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [list, setList] = useState<PlatformUser[]>(users);
  useEffect(() => { setList(users); }, [users]);
  const search = async () => {
    setBusy(true);
    setMessage("");
    try {
      const result = await callPlatformAdmin<PlatformUser[]>(session, "list_users", {
        query: query.trim() || undefined,
        status: status || undefined,
      });
      setList(result);
      setMessage(`共 ${result.length} 位用户`);
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "搜索失败");
    } finally {
      setBusy(false);
    }
  };
  const changeStatus = async (id: string, next: "active" | "suspended" | "banned") => {
    const reason = window.prompt(
      next === "active" ? "请填写解封原因" : "请填写限制/封禁原因",
    ) ?? "";
    if (!reason.trim()) return;
    setBusy(true);
    try {
      await callPlatformAdmin(session, "set_user_status", { user_id: id, status: next, reason });
      await onRefresh();
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "更新失败");
    } finally {
      setBusy(false);
    }
  };
  const changeRole = async (id: string, role: "user" | "admin") => {
    if (!window.confirm(`确认为该用户${role === "admin" ? "授予" : "移除"}管理员权限？`)) return;
    setBusy(true);
    try {
      await callPlatformAdmin(session, "set_user_role", { user_id: id, role });
      await onRefresh();
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "更新失败");
    } finally {
      setBusy(false);
    }
  };
  return (
    <section>
      <h2 className="text-2xl font-semibold">用户管理</h2>
      <p className="mt-1 text-sm text-slate-500">
        可管理账号状态和角色，不显示联系人、备注或关系档案。
      </p>
      <div className="mt-4 flex flex-wrap gap-2">
        <input
          className="rounded-lg border px-3 py-2 text-sm"
          placeholder="搜索邮箱 / 姓名 / 手机"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") void search(); }}
        />
        <select className="rounded-lg border px-3 py-2 text-sm" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">全部状态</option>
          <option value="active">正常</option>
          <option value="suspended">已限制</option>
          <option value="banned">已封禁</option>
        </select>
        <button className="rounded-lg bg-teal-700 px-4 py-2 text-sm text-white disabled:opacity-50" disabled={busy} onClick={() => void search()}>
          搜索
        </button>
        <button className="rounded-lg border px-4 py-2 text-sm" disabled={busy} onClick={() => void onRefresh()}>
          重置列表
        </button>
      </div>
      {message && <p className="mt-3 text-sm text-slate-600">{message}</p>}
      <div className="mt-4 overflow-x-auto rounded-xl border bg-white">
        <table className="w-full min-w-[720px] text-left text-sm">
          <thead className="bg-slate-100">
            <tr>
              <th className="px-4 py-3">用户</th>
              <th className="px-4 py-3">联系方式</th>
              <th className="px-4 py-3">角色</th>
              <th className="px-4 py-3">状态</th>
              <th className="px-4 py-3">操作</th>
            </tr>
          </thead>
          <tbody>
            {list.map((user) => (
              <tr key={user.id} className="border-t">
                <td className="px-4 py-3">
                  <div className="font-medium">{user.display_name || "未设置姓名"}</div>
                  <div className="text-xs text-slate-500">{user.id}</div>
                  {user.status_reason && <div className="mt-1 text-xs text-amber-700">原因：{user.status_reason}</div>}
                </td>
                <td className="px-4 py-3">{user.email || user.phone_e164 || "—"}</td>
                <td className="px-4 py-3">
                  <select
                    className="rounded border px-2 py-1 text-xs"
                    value={user.role}
                    disabled={busy || user.id === session.user.id}
                    onChange={(e) => void changeRole(user.id, e.target.value as "user" | "admin")}
                  >
                    <option value="user">普通用户</option>
                    <option value="admin">管理员</option>
                  </select>
                </td>
                <td className="px-4 py-3">
                  {user.status === "active" ? "正常" : user.status === "banned" ? "已封禁" : "已限制"}
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {user.status === "active" ? (
                      <>
                        <button className="rounded border px-2 py-1 text-xs" disabled={busy || user.id === session.user.id} onClick={() => void changeStatus(user.id, "suspended")}>限制</button>
                        <button className="rounded border border-red-200 px-2 py-1 text-xs text-red-700" disabled={busy || user.id === session.user.id} onClick={() => void changeStatus(user.id, "banned")}>封禁</button>
                      </>
                    ) : (
                      <button className="rounded border px-2 py-1 text-xs" disabled={busy || user.id === session.user.id} onClick={() => void changeStatus(user.id, "active")}>解封</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {list.length === 0 && (
          <p className="p-8 text-center text-sm text-slate-500">暂无用户</p>
        )}
      </div>
    </section>
  );
}

function ActivitiesPage({
  activities,
  session,
  onRefresh,
}: {
  activities: PlatformActivity[];
  session: Session;
  onRefresh: () => Promise<void>;
}) {
  const [items, setItems] = useState<PlatformActivity[]>(activities);
  useEffect(() => { setItems(activities); }, [activities]);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [city, setCity] = useState("");
  const [location, setLocation] = useState("");
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [capacity, setCapacity] = useState("0");

  const reload = async (nextStatus = statusFilter) => {
    setBusy(true);
    setMessage("");
    try {
      const data = await callPlatformAdmin<PlatformActivity[]>(session, "list_activities", nextStatus ? { status: nextStatus } : {});
      setItems(data);
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "读取失败");
    } finally {
      setBusy(false);
    }
  };
  const review = async (id: string, next: "approved" | "rejected" | "removed") => {
    const reason = next === "approved" ? "审核通过" : window.prompt("请填写驳回/下架原因", "违反平台活动规范") ?? "";
    if (next !== "approved" && !reason.trim()) return;
    setBusy(true);
    try {
      await callPlatformAdmin(session, "review_activity", { activity_id: id, status: next, reason });
      await reload();
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "审核失败");
    } finally {
      setBusy(false);
    }
  };
  const remove = async (id: string) => {
    if (!window.confirm("确定彻底删除该活动？删除后不可恢复。")) return;
    setBusy(true);
    try {
      await callPlatformAdmin(session, "delete_activity", { activity_id: id });
      await reload();
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "删除失败");
    } finally {
      setBusy(false);
    }
  };
  const publishOfficial = async () => {
    const validationError = validateOfficialActivity({
      title,
      city,
      location,
      startsAt,
      endsAt,
      capacity,
    });
    if (validationError) {
      setMessage(validationError);
      return;
    }
    setBusy(true);
    try {
      await callPlatformAdmin(session, "create_official_activity", {
        title, description, city, location,
        starts_at: new Date(startsAt).toISOString(),
        ends_at: new Date(endsAt).toISOString(),
        capacity: Number(capacity),
      });
      setTitle(""); setDescription(""); setCity(""); setLocation("");
      setStartsAt(""); setEndsAt(""); setCapacity("0");
      setStatusFilter("");
      await reload("");
      setMessage("官方活动已发布");
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "发布失败");
    } finally {
      setBusy(false);
    }
  };
  return (
    <section>
      <h2 className="text-2xl font-semibold">活动审核</h2>
      <p className="mt-1 text-sm text-slate-500">
        审核公开活动内容；用户私人联系人和关系数据不会被提交。
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        {(["", "pending", "approved", "rejected", "removed", "draft"] as const).map((s) => (
          <button
            key={s || "all"}
            disabled={busy}
            onClick={() => { setStatusFilter(s); void reload(s); }}
            className={`rounded-full px-3 py-1.5 text-xs ${statusFilter === s ? "bg-teal-700 text-white" : "border bg-white"}`}
          >
            {s ? activityStatusLabel[s as PlatformActivity["status"]] : "全部"}
          </button>
        ))}
        <button className="ml-auto rounded-lg border px-3 py-1.5 text-xs" disabled={busy} onClick={() => void onRefresh()}>
          刷新
        </button>
      </div>
      {message && <p className="mt-3 text-sm text-slate-600">{message}</p>}
      <div className="mt-6 grid gap-3 rounded-xl border bg-white p-5 sm:grid-cols-2">
        <h3 className="font-semibold sm:col-span-2">发布官方活动</h3>
        <input className="rounded-lg border px-3 py-2" placeholder="活动名称" value={title} onChange={(e) => setTitle(e.target.value)} />
        <input className="rounded-lg border px-3 py-2" placeholder="城市" value={city} onChange={(e) => setCity(e.target.value)} />
        <input className="rounded-lg border px-3 py-2" placeholder="地点" value={location} onChange={(e) => setLocation(e.target.value)} />
        <input className="rounded-lg border px-3 py-2" type="number" placeholder="人数上限" value={capacity} onChange={(e) => setCapacity(e.target.value)} />
        <input className="rounded-lg border px-3 py-2" type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
        <input className="rounded-lg border px-3 py-2" type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
        <textarea className="rounded-lg border px-3 py-2 sm:col-span-2" placeholder="活动说明" value={description} onChange={(e) => setDescription(e.target.value)} />
        <button className="rounded-lg bg-teal-700 px-4 py-2 text-sm text-white disabled:opacity-50 sm:col-span-2" disabled={busy} onClick={() => void publishOfficial()}>
          立即发布官方活动
        </button>
      </div>
      <div className="mt-6 space-y-4">
        {items.map((activity) => (
          <article key={activity.id} className="rounded-xl border bg-white p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="font-semibold">{activity.title}</h3>
                <p className="mt-1 text-sm text-slate-500">
                  {activity.city} · {activity.location} · {new Date(activity.starts_at).toLocaleString()} 至 {new Date(activity.ends_at).toLocaleString()}
                </p>
                {activity.moderation_reason && (
                  <p className="mt-1 text-xs text-amber-700">审核备注：{activity.moderation_reason}</p>
                )}
              </div>
              <span className="shrink-0 rounded-full bg-slate-100 px-3 py-1 text-xs">
                {activityStatusLabel[activity.status]}
              </span>
            </div>
            <p className="mt-4 whitespace-pre-wrap text-sm">{activity.description || "（无说明）"}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <button className="rounded-lg bg-teal-700 px-3 py-2 text-sm text-white disabled:opacity-50" disabled={busy} onClick={() => void review(activity.id, "approved")}>
                批准发布
              </button>
              <button className="rounded-lg border border-amber-300 px-3 py-2 text-sm text-amber-700 disabled:opacity-50" disabled={busy} onClick={() => void review(activity.id, "rejected")}>
                驳回
              </button>
              <button className="rounded-lg border border-red-200 px-3 py-2 text-sm text-red-700 disabled:opacity-50" disabled={busy} onClick={() => void review(activity.id, "removed")}>
                下架
              </button>
              <button className="rounded-lg border px-3 py-2 text-sm text-slate-500 disabled:opacity-50" disabled={busy} onClick={() => void remove(activity.id)}>
                彻底删除
              </button>
            </div>
          </article>
        ))}
        {items.length === 0 && (
          <div className="rounded-xl border bg-white p-8 text-center text-sm text-slate-500">
            该状态下暂无活动
          </div>
        )}
      </div>
    </section>
  );
}

function AlertsPage({ alerts, session, onRefresh }: { alerts: PlatformAlert[]; session: Session; onRefresh: () => Promise<void> }) {
  const [items, setItems] = useState<PlatformAlert[]>(alerts);
  useEffect(() => { setItems(alerts); }, [alerts]);
  const [filter, setFilter] = useState("open");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const reload = async (next = filter) => {
    setBusy(true);
    try {
      const data = await callPlatformAdmin<PlatformAlert[]>(session, "list_alerts", next ? { status: next } : {});
      setItems(data);
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "读取失败");
    } finally {
      setBusy(false);
    }
  };
  const update = async (id: string, next: PlatformAlert["status"]) => {
    setBusy(true);
    try {
      await callPlatformAdmin(session, "update_alert_status", { alert_id: id, status: next });
      await reload();
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "更新失败");
    } finally {
      setBusy(false);
    }
  };
  return (
    <section>
      <h2 className="text-2xl font-semibold">内容预警</h2>
      <p className="mt-1 text-sm text-slate-500">
        活动命中敏感词后自动生成预警。只记录命中词 ID 与风险等级，不复制用户隐私内容。
      </p>
      <div className="mt-4 flex gap-2">
        {(["", "open", "acknowledged", "resolved"] as const).map((s) => (
          <button key={s || "all"} disabled={busy} onClick={() => { setFilter(s); void reload(s); }}
            className={`rounded-full px-3 py-1.5 text-xs ${filter === s ? "bg-teal-700 text-white" : "border bg-white"}`}>
            {s ? alertStatusLabel[s as PlatformAlert["status"]] : "全部"}
          </button>
        ))}
        <button className="ml-auto rounded-lg border px-3 py-1.5 text-xs" onClick={() => void onRefresh()}>刷新</button>
      </div>
      {message && <p className="mt-3 text-sm text-slate-600">{message}</p>}
      <div className="mt-4 space-y-3">
        {items.map((alert) => (
          <div key={alert.id} className="rounded-xl border bg-white p-4 text-sm">
            <div className="flex items-center justify-between gap-3">
              <p className="font-medium">{alert.activity_title || alert.activity_id}</p>
              <span className={`rounded-full px-3 py-1 text-xs ${alert.status === "open" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-600"}`}>
                {alertStatusLabel[alert.status]} · 风险 {alert.highest_severity}
              </span>
            </div>
            <p className="mt-1 text-xs text-slate-500">
              {alert.activity_city} · 命中 {alert.matched_word_ids.length} 个词 · {new Date(alert.created_at).toLocaleString()}
            </p>
            <div className="mt-3 flex gap-2">
              <button className="rounded border px-2 py-1 text-xs" disabled={busy} onClick={() => void update(alert.id, "acknowledged")}>确认</button>
              <button className="rounded border px-2 py-1 text-xs" disabled={busy} onClick={() => void update(alert.id, "resolved")}>解决</button>
              <button className="rounded border px-2 py-1 text-xs" disabled={busy} onClick={() => void update(alert.id, "open")}>重开</button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="rounded-xl border bg-white p-8 text-center text-sm text-slate-500">暂无预警</p>}
      </div>
    </section>
  );
}

export function WordsPage({
  words,
  session,
  onRefresh,
}: {
  words: SensitiveWord[];
  session: Session;
  onRefresh: () => Promise<void>;
}) {
  const [phrase, setPhrase] = useState("");
  const [severity, setSeverity] = useState("3");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const lastSyncAt = useRef(0);
  const syncButton = useRef<HTMLButtonElement>(null);
  const lastPointerAt = useRef(0);
  useEffect(() => {
    const sync = () => {
      if (document.visibilityState !== "visible") return;
      if (document.activeElement === syncButton.current) return;
      if (Date.now() - lastPointerAt.current < 500) return;
      const now = Date.now();
      if (now - lastSyncAt.current < 250) return;
      lastSyncAt.current = now;
      void onRefresh();
    };
    const timer = window.setInterval(sync, 30000);
    window.addEventListener("focus", sync);
    const rememberPointer = () => {
      lastPointerAt.current = Date.now();
    };
    document.addEventListener("pointerdown", rememberPointer, true);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", sync);
      document.removeEventListener("pointerdown", rememberPointer, true);
    };
  }, [onRefresh]);
  const save = async (values: Record<string, unknown>, clearInput = false) => {
    if (saving) return;
    setSaving(true);
    setSaveError("");
    try {
      await callPlatformAdmin(session, "upsert_sensitive_word", values);
      if (clearInput) setPhrase("");
      await onRefresh();
    } catch (cause) {
      setSaveError(
        cause instanceof Error ? cause.message : "保存敏感词失败，请重试",
      );
    } finally {
      setSaving(false);
    }
  };
  const add = async () => {
    if (!phrase.trim()) return;
    await save(
      {
        phrase: phrase.trim(),
        severity: Number(severity),
        enabled: true,
      },
      true,
    );
  };
  const remove = async (id: string) => {
    if (!window.confirm("确定删除该敏感词？")) return;
    setSaving(true);
    try {
      await callPlatformAdmin(session, "delete_sensitive_word", { id });
      await onRefresh();
    } catch (cause) {
      setSaveError(cause instanceof Error ? cause.message : "删除失败");
    } finally {
      setSaving(false);
    }
  };
  return (
    <section>
      <h2 className="text-2xl font-semibold">敏感词管理</h2>
      <p className="mt-1 text-sm text-slate-500">
        与活动审核共用云端词库，保存后对后续提交生效。页面每 30
        秒及重新聚焦时同步。
      </p>
      <button
        ref={syncButton}
        className="mt-3 rounded-lg border px-3 py-2 text-sm"
        onClick={() => void onRefresh()}
        disabled={saving}
      >
        同步词库
      </button>
      {saveError && (
        <p role="alert" className="mt-3 text-sm text-red-700">
          {saveError}
        </p>
      )}
      <div className="mt-6 flex gap-2">
        <input
          className="rounded-lg border px-3 py-2"
          placeholder="输入敏感词"
          aria-label="敏感词"
          maxLength={120}
          disabled={saving}
          value={phrase}
          onChange={(event) => setPhrase(event.target.value)}
        />
        <select
          className="rounded-lg border px-3 py-2"
          value={severity}
          aria-label="新词风险等级"
          disabled={saving}
          onChange={(event) => setSeverity(event.target.value)}
        >
          <option value="1">低风险</option>
          <option value="2">较低风险</option>
          <option value="3">中风险</option>
          <option value="4">较高风险</option>
          <option value="5">高风险</option>
        </select>
        <button
          className="rounded-lg bg-teal-700 px-4 py-2 text-sm text-white"
          onClick={add}
          disabled={saving || !phrase.trim()}
        >
          添加
        </button>
      </div>
      <div className="mt-6 overflow-hidden rounded-xl border bg-white">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-100">
            <tr>
              <th className="px-4 py-3">词语</th>
              <th className="px-4 py-3">风险等级</th>
              <th className="px-4 py-3">状态</th>
              <th className="px-4 py-3">操作</th>
            </tr>
          </thead>
          <tbody>
            {words.map((word) => (
              <tr key={word.id} className="border-t">
                <td className="px-4 py-3">{word.phrase}</td>
                <td className="px-4 py-3">
                  <select
                    aria-label={`${word.phrase}风险等级`}
                    value={word.severity}
                    disabled={saving}
                    onChange={(event) =>
                      void save({
                        ...word,
                        severity: Number(event.target.value),
                      })
                    }
                  >
                    {[1, 2, 3, 4, 5].map((level) => (
                      <option key={level} value={level}>
                        {level}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-4 py-3">{word.enabled ? "启用" : "停用"}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-3">
                    <button
                      className="text-teal-700"
                      disabled={saving}
                      aria-label={`${word.enabled ? "停用" : "启用"}${word.phrase}`}
                      onClick={() =>
                        void save({ ...word, enabled: !word.enabled })
                      }
                    >
                      {word.enabled ? "停用" : "启用"}
                    </button>
                    <button
                      className="text-red-700"
                      disabled={saving}
                      aria-label={`删除${word.phrase}`}
                      onClick={() => void remove(word.id)}
                    >
                      删除
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {words.length === 0 && (
              <tr>
                <td
                  colSpan={4}
                  className="px-4 py-8 text-center text-slate-500"
                >
                  词库暂无记录，添加后将用于活动审核。
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function AdsPage({ slots, ads, session, onRefresh }: { slots: AdSlot[]; ads: PlatformAd[]; session: Session; onRefresh: () => Promise<void> }) {
  const [items, setItems] = useState<PlatformAd[]>(ads);
  const [slotItems, setSlotItems] = useState<AdSlot[]>(slots);
  useEffect(() => { setItems(ads); setSlotItems(slots); }, [ads, slots]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [slotKey, setSlotKey] = useState("");
  const [slotLabel, setSlotLabel] = useState("");
  const [adSlotId, setAdSlotId] = useState("");
  const [adTitle, setAdTitle] = useState("");
  const [adBody, setAdBody] = useState("");
  const [adTarget, setAdTarget] = useState("");
  const saveSlot = async () => {
    if (!slotKey.trim() || !slotLabel.trim()) {
      setMessage("请填写广告位标识与名称");
      return;
    }
    setBusy(true);
    try {
      await callPlatformAdmin(session, "upsert_ad_slot", { key: slotKey, label: slotLabel, enabled: false });
      setSlotKey(""); setSlotLabel("");
      await onRefresh();
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "保存失败");
    } finally {
      setBusy(false);
    }
  };
  const toggleSlot = async (slot: AdSlot) => {
    setBusy(true);
    try {
      await callPlatformAdmin(session, "upsert_ad_slot", { id: slot.id, key: slot.key, label: slot.label, enabled: !slot.enabled });
      await onRefresh();
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "更新失败");
    } finally {
      setBusy(false);
    }
  };
  const removeSlot = async (id: string) => {
    if (!window.confirm("确定删除该广告位？已有广告关联时会拒绝删除。")) return;
    setBusy(true);
    try {
      await callPlatformAdmin(session, "delete_ad_slot", { id });
      await onRefresh();
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "删除失败");
    } finally {
      setBusy(false);
    }
  };
  const saveAd = async () => {
    if (!adSlotId || !adTitle.trim()) {
      setMessage("请选择广告位并填写广告标题");
      return;
    }
    setBusy(true);
    try {
      await callPlatformAdmin(session, "upsert_ad", {
        slot_id: adSlotId, title: adTitle, body: adBody, target_url: adTarget, enabled: false,
      });
      setAdTitle(""); setAdBody(""); setAdTarget("");
      await onRefresh();
      setMessage("广告已保存为停用状态，确认内容后再启用");
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "保存失败");
    } finally {
      setBusy(false);
    }
  };
  const toggleAd = async (ad: PlatformAd) => {
    setBusy(true);
    try {
      await callPlatformAdmin(session, "upsert_ad", {
        id: ad.id, slot_id: ad.slot_id, title: ad.title, body: ad.body,
        image_url: ad.image_url, target_url: ad.target_url,
        starts_at: ad.starts_at, ends_at: ad.ends_at, enabled: !ad.enabled,
      });
      await onRefresh();
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "更新失败");
    } finally {
      setBusy(false);
    }
  };
  const removeAd = async (id: string) => {
    if (!window.confirm("确定删除该广告？")) return;
    setBusy(true);
    try {
      await callPlatformAdmin(session, "delete_ad", { id });
      await onRefresh();
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "删除失败");
    } finally {
      setBusy(false);
    }
  };
  return (
    <section>
      <h2 className="text-2xl font-semibold">广告管理</h2>
      <p className="mt-1 text-sm text-slate-500">
        广告位与广告内容默认停用；仅已启用的广告位及其广告才对用户可见。手机端广告渲染接入前请保持停用。
      </p>
      {message && <p className="mt-3 text-sm text-slate-600">{message}</p>}
      <div className="mt-4 rounded-xl border bg-white p-5">
        <h3 className="font-semibold">新增广告位</h3>
        <div className="mt-3 flex flex-wrap gap-2">
          <input className="rounded-lg border px-3 py-2 text-sm" placeholder="标识，如 home_banner" value={slotKey} onChange={(e) => setSlotKey(e.target.value)} />
          <input className="rounded-lg border px-3 py-2 text-sm" placeholder="名称，如 首页横幅" value={slotLabel} onChange={(e) => setSlotLabel(e.target.value)} />
          <button className="rounded-lg bg-teal-700 px-4 py-2 text-sm text-white disabled:opacity-50" disabled={busy} onClick={() => void saveSlot()}>保存</button>
        </div>
        <div className="mt-4 space-y-2">
          {slotItems.map((slot) => (
            <div key={slot.id} className="flex items-center justify-between rounded-lg border px-3 py-2 text-sm">
              <span>{slot.label}（{slot.key}）· {slot.enabled ? "已启用" : "已停用"}</span>
              <div className="flex gap-1">
                <button className="rounded border px-2 py-1 text-xs" disabled={busy} onClick={() => void toggleSlot(slot)}>
                  {slot.enabled ? "停用" : "启用"}
                </button>
                <button className="rounded border border-red-200 px-2 py-1 text-xs text-red-700" disabled={busy} onClick={() => void removeSlot(slot.id)}>
                  删除
                </button>
              </div>
            </div>
          ))}
          {slotItems.length === 0 && <p className="text-sm text-slate-500">暂无广告位</p>}
        </div>
      </div>
      <div className="mt-4 rounded-xl border bg-white p-5">
        <h3 className="font-semibold">新增广告</h3>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          <select className="rounded-lg border px-3 py-2 text-sm" value={adSlotId} onChange={(e) => setAdSlotId(e.target.value)}>
            <option value="">选择广告位</option>
            {slotItems.map((s) => <option key={s.id} value={s.id}>{s.label}（{s.key}）</option>)}
          </select>
          <input className="rounded-lg border px-3 py-2 text-sm" placeholder="广告标题" value={adTitle} onChange={(e) => setAdTitle(e.target.value)} />
          <input className="rounded-lg border px-3 py-2 text-sm sm:col-span-2" placeholder="跳转链接（可选）" value={adTarget} onChange={(e) => setAdTarget(e.target.value)} />
          <textarea className="rounded-lg border px-3 py-2 text-sm sm:col-span-2" placeholder="广告正文" value={adBody} onChange={(e) => setAdBody(e.target.value)} />
          <button className="rounded-lg bg-teal-700 px-4 py-2 text-sm text-white disabled:opacity-50 sm:col-span-2" disabled={busy} onClick={() => void saveAd()}>保存广告</button>
        </div>
        <div className="mt-4 space-y-2">
          {items.map((ad) => (
            <div key={ad.id} className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2 text-sm">
              <div>
                <p className="font-medium">{ad.title}</p>
                <p className="text-xs text-slate-500">{ad.slot_label || ad.slot_id} · {ad.enabled ? "已启用" : "已停用"}</p>
              </div>
              <div className="flex gap-1">
                <button className="rounded border px-2 py-1 text-xs" disabled={busy} onClick={() => void toggleAd(ad)}>{ad.enabled ? "停用" : "启用"}</button>
                <button className="rounded border border-red-200 px-2 py-1 text-xs text-red-700" disabled={busy} onClick={() => void removeAd(ad.id)}>删除</button>
              </div>
            </div>
          ))}
          {items.length === 0 && <p className="text-sm text-slate-500">暂无广告</p>}
        </div>
      </div>
    </section>
  );
}

function AuditPage({ logs, onRefresh }: { logs: AuditLog[]; onRefresh: () => Promise<void> }) {
  return (
    <section>
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold">审计日志</h2>
          <p className="mt-1 text-sm text-slate-500">
            所有管理员变更都会写入审计日志，日志只读不可改写。按时间倒序展示最近 200 条。
          </p>
        </div>
        <button className="rounded-lg border px-3 py-2 text-sm" onClick={() => void onRefresh()}>刷新</button>
      </div>
      <div className="mt-4 overflow-x-auto rounded-xl border bg-white">
        <table className="w-full min-w-[720px] text-left text-sm">
          <thead className="bg-slate-100">
            <tr>
              <th className="px-4 py-3">时间</th>
              <th className="px-4 py-3">操作</th>
              <th className="px-4 py-3">对象</th>
              <th className="px-4 py-3">执行人</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((log) => (
              <tr key={log.id} className="border-t">
                <td className="px-4 py-3 text-xs">{new Date(log.created_at).toLocaleString()}</td>
                <td className="px-4 py-3 font-mono text-xs">{log.action}</td>
                <td className="px-4 py-3 text-xs">{log.target_type} / {log.target_id}</td>
                <td className="px-4 py-3 text-xs">{log.actor_name || log.actor_email || log.actor_id}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {logs.length === 0 && <p className="p-8 text-center text-sm text-slate-500">暂无审计记录</p>}
      </div>
    </section>
  );
}

// 短信模板的用途（验证码/通知/营销）与模板变量名保存在 config 里，密钥只留引用名。
function buildSmsConfig(kind: "sms" | "oauth", templateType: string, signature: string) {
  if (kind !== "sms") return {};
  return {
    template_type: templateType,
    template_param_key: "code",
    region_id: "cn-hangzhou",
    has_signature: signature.trim().length > 0,
  };
}

function SettingsPage({
  providers,
  session,
  onRefresh,
}: {
  providers: AuthProviderSetting[];
  session: Session;
  onRefresh: () => Promise<void>;
}) {
  const [kind, setKind] = useState<"sms" | "oauth">("sms");
  const [providerKey, setProviderKey] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [endpoint, setEndpoint] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [signature, setSignature] = useState("");
  const [credentialRef, setCredentialRef] = useState("");
  const [templateType, setTemplateType] = useState("verification");
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [testPhone, setTestPhone] = useState("");

  const useAliyunPreset = () => {
    setKind("sms");
    setProviderKey(ALIYUN_SMS_PRESET.provider_key);
    setDisplayName(ALIYUN_SMS_PRESET.display_name);
    setEndpoint(ALIYUN_SMS_PRESET.endpoint);
    setTemplateId(ALIYUN_SMS_PRESET.template_id);
    setTemplateType(ALIYUN_SMS_PRESET.template_type);
    setCredentialRef(ALIYUN_SMS_PRESET.credential_ref);
    setMessage("已填入阿里云短信默认配置，请补充短信签名后保存");
  };

  const sendTest = async (providerKey: string) => {
    if (!testPhone.trim()) {
      setMessage("请填写接收测试短信的手机号");
      return;
    }
    setBusy(true);
    try {
      const result = await sendTestSms(session, testPhone.trim(), providerKey);
      setMessage(
        `测试短信已发送${result.test_code ? `，验证码 ${result.test_code}` : ""}（模板 ${result.template_code ?? ""}）`,
      );
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "测试短信发送失败");
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!providerKey.trim() || !displayName.trim()) {
      setMessage("请填写服务标识与显示名称");
      return;
    }
    setBusy(true);
    try {
      await callPlatformAdmin(session, "upsert_auth_provider", {
        kind,
        provider_key: providerKey,
        display_name: displayName,
        endpoint,
        template_id: templateId,
        signature,
        credential_ref: credentialRef,
        enabled,
        config: buildSmsConfig(kind, templateType, signature),
      });
      setProviderKey("");
      setDisplayName("");
      setEndpoint("");
      setTemplateId("");
      setSignature("");
      setCredentialRef("");
      setTemplateType("verification");
      setEnabled(false);
      await onRefresh();
      setMessage("已保存");
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "保存失败");
    } finally {
      setBusy(false);
    }
  };
  const toggle = async (p: AuthProviderSetting) => {
    setBusy(true);
    try {
      await callPlatformAdmin(session, "upsert_auth_provider", {
        id: p.id, kind: p.kind, provider_key: p.provider_key, display_name: p.display_name,
        endpoint: p.endpoint, template_id: p.template_id, signature: p.signature,
        credential_ref: p.credential_ref,
        config: buildSmsConfig(p.kind, readTemplateType(p), p.signature),
        enabled: !p.enabled,
      });
      await onRefresh();
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "更新失败");
    } finally {
      setBusy(false);
    }
  };
  const remove = async (id: string) => {
    if (!window.confirm("确定删除该登录服务配置？")) return;
    setBusy(true);
    try {
      await callPlatformAdmin(session, "delete_auth_provider", { id });
      await onRefresh();
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "删除失败");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section>
      <h2 className="text-2xl font-semibold">登录与短信设置</h2>
      <p className="mt-1 text-sm text-slate-500">
        配置手机号验证码、华为和微信登录。密钥只引用部署平台的安全变量，不会返回到页面。
      </p>
      <PasswordChangeForm email={session.user.email} />
      {message && <p className="mt-3 text-sm text-slate-600">{message}</p>}
      <div className="mt-4 grid max-w-2xl gap-3 rounded-xl border bg-white p-5 sm:grid-cols-2">
        <select
          className="rounded-lg border px-3 py-2"
          value={kind}
          onChange={(event) => setKind(event.target.value as "sms" | "oauth")}
        >
          <option value="sms">短信服务商</option>
          <option value="oauth">OAuth 登录</option>
        </select>
        <input
          className="rounded-lg border px-3 py-2"
          placeholder="服务标识，如 aliyun"
          value={providerKey}
          onChange={(event) => setProviderKey(event.target.value)}
        />
        <input
          className="rounded-lg border px-3 py-2"
          placeholder="显示名称"
          value={displayName}
          onChange={(event) => setDisplayName(event.target.value)}
        />
        <input
          className="rounded-lg border px-3 py-2"
          placeholder="服务地址（可选）"
          value={endpoint}
          onChange={(event) => setEndpoint(event.target.value)}
        />
        <input
          className="rounded-lg border px-3 py-2"
          placeholder="短信模板 ID（短信用）"
          value={templateId}
          onChange={(event) => setTemplateId(event.target.value)}
        />
        <input
          className="rounded-lg border px-3 py-2"
          placeholder="短信签名（短信用，须与阿里云已审核签名一致）"
          value={signature}
          onChange={(event) => setSignature(event.target.value)}
        />
        {kind === "sms" && (
          <select
            className="rounded-lg border px-3 py-2"
            value={templateType}
            onChange={(event) => setTemplateType(event.target.value)}
          >
            {SMS_TEMPLATE_TYPES.map((item) => (
              <option key={item.value} value={item.value}>模板类型：{item.label}</option>
            ))}
          </select>
        )}
        <input
          className="rounded-lg border px-3 py-2"
          placeholder="密钥引用名，如 SMS_SECRET"
          value={credentialRef}
          onChange={(event) => setCredentialRef(event.target.value)}
        />
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(event) => setEnabled(event.target.checked)}
          />
          启用
        </label>
        <button
          className="rounded-lg bg-teal-700 px-4 py-2 text-sm text-white disabled:opacity-50 sm:col-span-2"
          disabled={busy}
          onClick={() => void save()}
        >
          保存配置
        </button>
      </div>
      {kind === "sms" && (
        <button
          className="mt-3 rounded-lg border border-teal-700 px-4 py-2 text-sm text-teal-800 disabled:opacity-50"
          disabled={busy}
          onClick={useAliyunPreset}
        >
          填入阿里云短信默认配置（模板 SMS_501605457）
        </button>
      )}
      <div className="mt-6 overflow-x-auto rounded-xl border bg-white">
        <table className="w-full min-w-[640px] text-left text-sm">
          <thead className="bg-slate-100">
            <tr>
              <th className="px-4 py-3">类型</th>
              <th className="px-4 py-3">服务</th>
              <th className="px-4 py-3">标识</th>
              <th className="px-4 py-3">模板</th>
              <th className="px-4 py-3">状态</th>
              <th className="px-4 py-3">密钥</th>
              <th className="px-4 py-3">操作</th>
            </tr>
          </thead>
          <tbody>
            {providers.map((provider) => (
              <tr key={provider.id} className="border-t">
                <td className="px-4 py-3">
                  {provider.kind === "sms" ? "短信" : "OAuth"}
                </td>
                <td className="px-4 py-3">{provider.display_name}</td>
                <td className="px-4 py-3 font-mono text-xs">{provider.provider_key}</td>
                <td className="px-4 py-3 text-xs">
                  {provider.kind === "sms" ? (
                    <>
                      <div className="font-mono">{provider.template_id || "未设置"}</div>
                      <div className="text-slate-500">
                        {SMS_TEMPLATE_TYPES.find((item) => item.value === readTemplateType(provider))?.label ?? "验证码"}
                        {provider.signature ? ` · ${provider.signature}` : " · 未填签名"}
                      </div>
                    </>
                  ) : (
                    "—"
                  )}
                </td>
                <td className="px-4 py-3">
                  {provider.enabled ? "启用" : "停用"}
                </td>
                <td className="px-4 py-3">
                  {provider.credential_ref || "未配置"}
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <button className="rounded border px-2 py-1 text-xs" disabled={busy} onClick={() => void toggle(provider)}>
                      {provider.enabled ? "停用" : "启用"}
                    </button>
                    {provider.kind === "sms" && provider.enabled && (
                      <button className="rounded border px-2 py-1 text-xs" disabled={busy} onClick={() => void sendTest(provider.provider_key)}>
                        测试
                      </button>
                    )}
                    <button className="rounded border border-red-200 px-2 py-1 text-xs text-red-700" disabled={busy} onClick={() => void remove(provider.id)}>
                      删除
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {providers.length === 0 && <p className="p-8 text-center text-sm text-slate-500">暂无登录服务配置</p>}
      </div>
      <div className="mt-4 flex max-w-xl flex-wrap items-center gap-2 rounded-xl border bg-white p-4">
        <input
          className="min-w-[220px] flex-1 rounded-lg border px-3 py-2 text-sm"
          placeholder="接收测试短信的手机号，如 13800000000"
          value={testPhone}
          onChange={(event) => setTestPhone(event.target.value)}
        />
        <button
          className="rounded-lg border border-teal-700 px-4 py-2 text-sm text-teal-800 disabled:opacity-50"
          disabled={busy || !providers.some((provider) => provider.kind === "sms" && provider.enabled)}
          onClick={() => void sendTest("aliyun")}
        >
          发送测试短信
        </button>
        <p className="w-full text-xs text-slate-500">
          测试短信走真实模板与限流规则，验证码只在后台回显一次，不会写入日志。
        </p>
      </div>
    </section>
  );
}
